import isEmail from 'validator/es/lib/isEmail';
import isNumeric from 'validator/es/lib/isNumeric';
import isEmpty from 'validator/es/lib/isEmpty';
import isMobilePhone from 'validator/es/lib/isMobilePhone';

export default {
  createFieldObject(type, label, defaultValue = '', constraints = {}, options = [], settings = {}) {
    return {
      type,
      label,
      options,
      constraints,
      value: defaultValue,
      defaultValue,
      error: '',
      disabled: false,
      ...settings,
    };
  },
  /**
   * Поле-справочник (BaseDir). Помимо обычных настроек поля хранит конфиг
   * сущности и предварительный фильтр списка — их BaseField прокидывает
   * в компонент справочника.
   */
  createDirFieldObject(label, config, settings = {}) {
    const { filter = {}, context = {}, defaultValue = null, constraints = {}, ...rest } = settings; // eslint-disable-line
    return this.createFieldObject('dir', label, defaultValue, constraints, [], { config, filter, context, ...rest }); // eslint-disable-line
  },
  /**
   * Поле выбора пользователя (BaseUserField). users — те, из кого можно
   * выбирать; наружу поле отдаёт IRI выбранного пользователя.
   */
  createUserFieldObject(label, users = [], settings = {}) {
    const { defaultValue = null, constraints = {}, ...rest } = settings;
    return this.createFieldObject('user', label, defaultValue, constraints, users, rest);
  },
  makeSelectOptionsFromArray(options) {
    return options.map((value) => ({ value, label: value }));
  },
  makeSelectOptionsFromObject(options) {
    return Object.entries(options).map(([value, label]) => ({ value, label }));
  },
  validate(fields) {
    Object.keys(fields).forEach((fieldName) => {
      this.validateField(fields, fieldName);
    });
  },
  validateField(fields, fieldName) {
    const field = fields[fieldName];

    if (!field) return;

    field.error = '';

    // eslint-disable-next-line
    Object.entries(field.constraints).some(([constraint, options]) => {
      const message = typeof options === 'string' ? options : options.message;
      const valueIsValid = this.constraints[constraint](field.value, options, fields);
      if (!valueIsValid) {
        field.error = message;
        return true; // break;
      }
    });
  },
  constraints: {
    // пустое значение проверку формата проходит: обязательность — дело
    // отдельного ограничения required, а без этого необязательное поле почты
    // (например, у клиента CRM) нельзя было оставить пустым
    email: (val) => val === '' || val === null || val === undefined || isEmail(val),
    match: (val, options, fields) => val === fields[options.field].value,
    number: (val) => val === '' || isNumeric(val.toString()),
    phone: (val) => isMobilePhone(val.replaceAll(/(^\+7|^8|\D)/g, ''), 'ru-RU'),
    // isEmpty() из validator бросается на не-строке, поэтому пустоту у null и
    // у числовых значений (например, у выпадающего списка) разбираем сами
    required: (val) => {
      if (typeof val === 'boolean') return val;
      if (val === null || val === undefined) return false;

      return !isEmpty(val.toString());
    },
  },
  hasErrors(fields) {
    return Object.values(fields).reduce((acc, curr) => acc || !!curr.error, false);
  },
  getValues(fields) {
    const getCastedValue = (field) => {
      switch (field.type) {
        case 'float':     return field.value === '' ? null : parseFloat(field.value);     // eslint-disable-line
        case 'int':       return field.value === '' ? null : parseInt(field.value, 10);   // eslint-disable-line
        case 'date':      return field.value === '' ? null : field.value;                 // eslint-disable-line
        case 'datetime':  return field.value === '' ? null : field.value;                 // eslint-disable-line
        case 'dir':       return field.value === '' ? null : field.value;                 // eslint-disable-line
        default:          return field.value;                                             // eslint-disable-line
      }
    };

    return Object.fromEntries(
      Object.entries(fields)
        .filter(([name, field]) => !!field)
        .map(([name, field]) => [name, getCastedValue(field)]),
    );
  },
  setValues(fields, source) {
    Object.keys(fields).forEach((fieldName) => {
      const field = fields[fieldName];
      const value = source[fieldName];

      if (value) {
        field.value = value;
      }
    });
  },
  clear(fields) {
    Object.keys(fields).forEach((fieldName) => {
      const field = fields[fieldName];
      field.error = '';
      field.value = field.defaultValue ?? '';
    });
  },
};
